/* @flow */
import * as React from 'react';
import PropTypes from 'prop-types';
import idx from 'idx';
import { graphql, compose } from 'react-apollo';
import isUuid from 'validator/lib/isUUID';

import { rootGroupCode } from 'entities/RootGroup';
import { withRouter } from 'app/context/Router';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import NotFoundPage from 'app/components/NotFoundPage';
import Group from 'entities/Group';
import GroupStore from 'groups/stores/GroupStore';

import LOAD_BY_GROUP_CODE_QUERY from '../queries/LoadByGroupCodeQuery.gql';
import NONV2GROUP_BY_UUID_QUERY from 'groups/queries/GroupByUUIDQuery.gql';

import type { LoadGroupByCode } from '../queries/__generated__/LoadGroupByCode';
import type { GroupByUUID } from 'groups/queries/__generated__/GroupByUUID';
import type { Location } from 'lib/ReactRouterTypes';

type Props = {
  params: Object,
  router: Object,
  location: Location,
  children: React.Node,
  groupByCode: ReactApolloData<LoadGroupByCode>,
  groupByUuid: ReactApolloData<GroupByUUID>,
};

type State = {
  group: ?Group,
  errorCode: ?string,
};

// this components is responsible for loading and passing a
// a backbone group instance into all its children props.
class GroupContainer extends React.Component<Props, State> {
  state = {
    group: null,
    errorCode: null,
  };

  static childContextTypes = {
    group: PropTypes.instanceOf(Group),
  };

  static getDerivedStateFromProps(nextProps: Props) {
    if (nextProps.params.groupCode === rootGroupCode) {
      const group = GroupStore.getGroupForCode(rootGroupCode);
      return { group };
    }

    const { groupByCode, groupByUuid } = nextProps;
    if ((groupByCode && groupByCode.loading) || (groupByUuid && groupByUuid.loading)) {
      return null;
    }

    const errorCode = idx(
      groupByCode || groupByUuid,
      _ => _.error.graphQLErrors[0].serviceMetadata.service_response.code
    );

    if (errorCode) {
      return { errorCode };
    }

    if (groupByCode) {
      const group = idx(groupByCode, _ => _.classes[0]);
      if (group) {
        return {
          group: ((Group.fromJSON(group): any): Group),
        };
      }
    }

    if (groupByUuid) {
      return {
        group: groupByUuid.nonV2Class,
      };
    }

    return null;
  }

  getChildContext() {
    return {
      group: this.state.group,
    };
  }

  render() {
    const { groupByCode, groupByUuid, params, children, router } = this.props;
    const { group, errorCode } = this.state;

    if ((groupByCode && groupByCode.loading) || (groupByUuid && groupByUuid.loading)) {
      return <CenteredLoadingIndicator />;
    }

    if (errorCode === 'class_not_found') {
      router.replace({ pathname: '/classes/join', query: { code: params.groupCode } });
      return <NotFoundPage />;
    }

    if (!group) {
      console.error('GroupContainer - failed to load group');
      console.warn(errorCode);
      return <NotFoundPage />;
    }

    // We are only using uuids to redirect to usual route, in the future will be uuid only
    if (isUuid(params.groupCode) && group.classCode) {
      router.replace({ pathname: `/classes/${group.classCode}`, query: {} });
      return null;
    }

    const groupCode = group.getCodeOrError();

    // When your request gets denied
    if (
      group.getType() === 'available' &&
      router.location.pathname.endsWith(`/${groupCode}/pending`)
    ) {
      // Redirect to default page
      router.replace({ pathname: '/classes', query: {} });
      return null;
    }

    if (
      group.getType() === 'pending' &&
      !router.location.pathname.endsWith(`/${groupCode}/pending`)
    ) {
      // Redirect to the pending group page
      router.replace({ pathname: `/classes/${groupCode}/pending`, query: {} });
      return null;
    }

    if (
      group.getType() !== 'pending' &&
      router.location.pathname.endsWith(`/${groupCode}/pending`)
    ) {
      // Redirect to the usual group page
      router.replace({ pathname: `/classes/${groupCode}`, query: {} });
      return null;
    }

    if (groupByCode || groupByUuid) {
      const { refetch } = groupByCode || groupByUuid;
      // $FlowFixMe
      return React.cloneElement(children, { group, refetch });
    } else {
      // $FlowFixMe
      return React.cloneElement(children, { group });
    }
  }
}

const withGroup = graphql(LOAD_BY_GROUP_CODE_QUERY, {
  name: 'groupByCode',
  skip: ({ params }) => params.groupCode === rootGroupCode || isUuid(params.groupCode),
  options: ({ params }) => ({
    variables: { code: `@${params.groupCode}` },
  }),
});

const withGroupByUuid = graphql(NONV2GROUP_BY_UUID_QUERY, {
  name: 'groupByUuid',
  skip: ({ params }) => params.groupCode === rootGroupCode || !isUuid(params.groupCode),
  options: ({ params }) => ({
    variables: { uuid: `${params.groupCode}` },
  }),
});

export { GroupContainer };
export default compose(withRouter, withGroup, withGroupByUuid)(GroupContainer);
